import { RootState, store } from "../../redux/store/store";
import {
  activeConfiguration,
  formatUrl,
  selectExternalConfigs,
  setIsSendingIxbrl,
  setSeahorseDocumentLinked,
} from "../../redux/feature/settings/configurations-slice";
import { validateString } from "../../utils/helpers/ObjectCheckerHelper";
import { axiosInstance } from "../../utils/axios";
import { ServiceProvider } from "../../api/models/service-provider";
import { SeahorseDocumentInfo } from "../../api/models/seahorse-document-info";
import { LinkedDocumentInfo } from "../../api/models/linked-document-info";
import { DocumentPart } from "../../api/models";
import { ExternalConfigsToSeahorse } from "../../api/models/external-configs";
import NotificationManager from "../../common/notification/NotificationManager";
import { IXBRL_NOTIFICATION } from "../../utils/constants/notification";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { IxbrlSetting } from "../../api/models/ixbrl-setting";

export function openSeahorseLink() {
  let backendUrl = getSeahorseBackendUrl();
  axiosInstance
    .get(backendUrl.concat(`/ui/api/co2/seahorse/seahorselink`))
    .then((resp) => {
      window.open("" + resp.data, "_blank");
      return resp.data;
    })
    .catch((reason) => console.error(`Error on fetching Seahorse Link`, reason));
}

export function sendDocumentToIXBRLUtil(documentInfo: SeahorseDocumentInfo): Promise<SeahorseDocumentInfo> {
  let backendUrl = getSeahorseBackendUrl();
  return axiosInstance
    .post(backendUrl.concat(`/ui/api/co2/seahorse/sendlinkeddocument`), documentInfo)
    .then((resp) => resp.data)
    .catch((reason) => console.error(`Error on fetching Seahorse Service Provider`, reason));
}

export function fetchServiceProvider(): Promise<ServiceProvider> {
  let backendUrl = getSeahorseBackendUrl();
  return axiosInstance
    .get(backendUrl.concat(`/ui/api/co2/seahorse/defaultserviceprovider`))
    .then((resp) => resp.data)
    .catch((reason) => console.error(`Error on fetching Seahorse Service Provider`, reason));
}

export function fetchSeahorseDocumentLinked(): Promise<LinkedDocumentInfo[]> {
  let backendUrl = getSeahorseBackendUrl();
  return axiosInstance
    .get(backendUrl.concat(`/ui/api/co2/seahorse/seahorselinkeddocumentlist`))
    .then((resp) => resp.data)
    .catch((reason) => console.error(`Error on fetching Seahorse Document Linked`, reason));
}

export function fetchIxbrlSettings(): Promise<IxbrlSetting[]> {
  let backendUrl = getSeahorseBackendUrl();
  return axiosInstance
    .get(backendUrl.concat(`/ui/api/co2/seahorse/ixbrlsettings`))
    .then((resp) => resp.data)
    .catch((reason) => console.error(`Error on fetching Ixbrl Settings`, reason));
}

export function fetchUpdateIxbrlSettings(documentId: string, isChecked: boolean): Promise<boolean> {
  let backendUrl = getSeahorseBackendUrl();
  return axiosInstance
    .get(backendUrl.concat(`/ui/api/co2/seahorse/updateixbrlsettings/${documentId}/${isChecked}`))
    .then((resp) => resp.data)
    .catch((reason) => console.error(`Error on fetching Update Ixbrl Settings`, reason));
}

export function hasNoDefaultServiceProvider(defaultServiceProvider: ServiceProvider): boolean {
  if (defaultServiceProvider) {
    return defaultServiceProvider.id <= 0;
  }
  return true;
}
export async function sendDocumentToIXBRL(selectedPart: DocumentPart, serviceProvider: number) {
  const state: RootState = store.getState();
  let externalConfigs = selectExternalConfigs(state);
  let configsToSeahorse: ExternalConfigsToSeahorse = {
    azureDefaultSite: externalConfigs.azureDefaultSite,
    azureTenantId: externalConfigs.azureTenantId,
    tenantId: externalConfigs.tenantId,
    sharePointHostName: externalConfigs.sharePointHostName,
  };
  let documentToSend: SeahorseDocumentInfo = {
    driveItemId: selectedPart.driveItemId,
    fileName: selectedPart.name,
    id: selectedPart.id,
    name: selectedPart.name,
    serviceProvider: serviceProvider,
    externalConfigs: configsToSeahorse,
  };
  await sendDocumentToIXBRLUtil(documentToSend).then((response) => {
    if (response !== undefined) {
      NotificationManager.addNotification({
        key: IXBRL_NOTIFICATION,
        message: formatMessage("cof.seahorse.senddocument.success"),
        title: "iXBRL",
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  });
}

/**
 * Centralized iXBRL send workflow with re-entry guard.
 * Prevents duplicate API calls, dispatches Redux state for UI disabling,
 * and refreshes linked documents on completion..
 */
export async function sendDocumentToIXBRLAndRefresh(selectedPart: DocumentPart, serviceProvider: number) {
  const currentState: RootState = store.getState();
  if (currentState.configurationState?.isSendingIxbrl) {
    return;
  }
  store.dispatch(setIsSendingIxbrl(true));
  try {
    await sendDocumentToIXBRL(selectedPart, serviceProvider);
    const result = await fetchSeahorseDocumentLinked();
    store.dispatch(setSeahorseDocumentLinked(result));
  } finally {
    store.dispatch(setIsSendingIxbrl(false));
  }
}

export function hasNoIxbrlSetting(documentId: string, allIxbrlSettings: IxbrlSetting[]): boolean {
  let noIxbrlSetting = true;
  if (allIxbrlSettings) {
    allIxbrlSettings.forEach((setting) => {
      if (documentId == setting.documentId) {
        noIxbrlSetting = !setting.isChecked;
      }
    });
  }
  return noIxbrlSetting;
}

function getSeahorseBackendUrl(): string {
  const state: RootState = store.getState();
  let activeConf = activeConfiguration(state.configurationState);
  let backendUrl = validateString(activeConf?.url) ? activeConf.url : formatUrl(activeConf?.tenantId);
  const CO_SERVICE = "co-service";
  if (backendUrl.indexOf(CO_SERVICE) != -1) {
    backendUrl = backendUrl.replace(CO_SERVICE, "co-seahorse");
  }
  if (backendUrl.indexOf("localhost") != -1) {
    backendUrl = "http://localhost:9095/co-seahorse";
  }
  return backendUrl;
}
